import { Container, Sprite, Texture } from 'pixi.js';
import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from '@/core/viewport';
import { type Weather, hasPrecipitation } from '@/time/weather';

/**
 * Rain and snow as a fixed pool of screen-space particles.
 *
 * Screen-space rather than world-space: precipitation fills the view no matter
 * where the camera is, so there is nothing to cull and no dependence on map
 * size. The pool is allocated once at the largest count any weather needs and
 * particles are hidden rather than destroyed, so switching weather allocates
 * nothing.
 */

interface Particle {
  sprite: Sprite;
  speedY: number;
  speedX: number;
  /** Snow drifts; rain does not. */
  wobble: number;
  phase: number;
}

const MAX_PARTICLES = 140;

const PROFILES: Readonly<
  Record<Weather, { count: number; speed: number; drift: number; alpha: number }>
> = {
  clear: { count: 0, speed: 0, drift: 0, alpha: 0 },
  cloudy: { count: 0, speed: 0, drift: 0, alpha: 0 },
  rain: { count: 90, speed: 260, drift: -40, alpha: 0.5 },
  storm: { count: 140, speed: 380, drift: -90, alpha: 0.62 },
  snow: { count: 70, speed: 42, drift: -8, alpha: 0.8 },
};

export class WeatherRenderer {
  readonly container = new Container();

  private readonly particles: Particle[] = [];
  private readonly rainTexture: Texture;
  private readonly snowTexture: Texture;
  private current: Weather = 'clear';

  constructor() {
    this.rainTexture = makeTexture(1, 6, 0xbfd8ea);
    this.snowTexture = makeTexture(2, 2, 0xf2f8ff);

    for (let i = 0; i < MAX_PARTICLES; i++) {
      const sprite = new Sprite(this.rainTexture);
      sprite.visible = false;
      this.container.addChild(sprite);
      this.particles.push({ sprite, speedY: 0, speedX: 0, wobble: 0, phase: 0 });
    }
  }

  update(weather: Weather, dt: number): void {
    if (weather !== this.current) {
      this.reconfigure(weather);
      this.current = weather;
    }

    if (!hasPrecipitation(weather)) return;

    const profile = PROFILES[weather];
    const isSnow = weather === 'snow';

    for (let i = 0; i < profile.count; i++) {
      const particle = this.particles[i]!;
      particle.phase += dt;

      const drift = isSnow ? Math.sin(particle.phase * particle.wobble) * 12 : 0;
      particle.sprite.x += (particle.speedX + drift) * dt;
      particle.sprite.y += particle.speedY * dt;

      // Wrap on both axes; a particle leaving the side re-enters opposite so
      // wind-blown rain does not thin out on one edge.
      if (particle.sprite.y > VIRTUAL_HEIGHT) {
        particle.sprite.y = -8;
        particle.sprite.x = Math.random() * VIRTUAL_WIDTH;
      }
      if (particle.sprite.x < -4) particle.sprite.x = VIRTUAL_WIDTH + 4;
      else if (particle.sprite.x > VIRTUAL_WIDTH + 4) particle.sprite.x = -4;
    }
  }

  destroy(): void {
    this.container.destroy({ children: true });
    this.rainTexture.destroy(true);
    this.snowTexture.destroy(true);
  }

  private reconfigure(weather: Weather): void {
    const profile = PROFILES[weather];
    const isSnow = weather === 'snow';
    const texture = isSnow ? this.snowTexture : this.rainTexture;

    for (let i = 0; i < MAX_PARTICLES; i++) {
      const particle = this.particles[i]!;
      const active = i < profile.count;

      particle.sprite.visible = active;
      if (!active) continue;

      particle.sprite.texture = texture;
      particle.sprite.alpha = profile.alpha * (0.7 + Math.random() * 0.3);
      particle.sprite.x = Math.random() * VIRTUAL_WIDTH;
      particle.sprite.y = Math.random() * VIRTUAL_HEIGHT;
      // Varying speed per particle reads as depth without a second layer.
      particle.speedY = profile.speed * (0.8 + Math.random() * 0.4);
      particle.speedX = profile.drift * (0.8 + Math.random() * 0.4);
      particle.wobble = 1.5 + Math.random() * 2;
      particle.phase = Math.random() * 10;
    }
  }
}

/**
 * Solid-colour texture from a canvas rather than a Graphics object: a texture
 * can be built before the renderer exists, and every particle shares one
 * texture so the pool batches into a single draw call.
 */
function makeTexture(width: number, height: number, colour: number): Texture {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('2D canvas context unavailable');
  context.fillStyle = `#${colour.toString(16).padStart(6, '0')}`;
  context.fillRect(0, 0, width, height);

  const texture = Texture.from(canvas);
  texture.source.scaleMode = 'nearest';
  return texture;
}
